import { Box, Typography, Stack, debounce, StackProps, Switch } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { defaultOptions } from './chartWidgetUtility';
import { SxProps, Theme } from '@mui/system';

interface CustomBarChartProps {
	title: any;
	isLoading?: boolean;
	data: any[];
	options: any;
	slaValue?: any;
	containerProps?: StackProps;
	isHappySignal?: boolean;
	showBottom10?: boolean;
	setShowBottom10?: any;
	isCost?: boolean;
	sx?: SxProps<Theme>;
  }

const calculateGridLeft = (containerWidth: number): string => {
  if (containerWidth < 420) return '18%';
  if (containerWidth < 600) return '15%';
  if (containerWidth < 820) return '12%';
  if (containerWidth < 920) return '9%';
  if (containerWidth < 1260) return '7%';
  return '5%';
};

const CustomBarChartWidget = ({
	title, isLoading, data, options, slaValue, containerProps, isHappySignal = false, showBottom10, setShowBottom10, isCost = false, sx
}: Readonly<CustomBarChartProps>) => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [, setGridLeft] = useState('5%');
  const chartContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// re-render chart to fix alignment when resizing the browser
		const handleResize = debounce(() => {
			if (!chartContainerRef.current) return;

			const containerWidth = chartContainerRef.current.offsetWidth;
			const calculatedLeft = calculateGridLeft(containerWidth);

			setGridLeft(calculatedLeft);
			setWindowSize(window.innerWidth);
		}, 200);

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const adjustedOption: any = !data?.length
		? defaultOptions
		: {
			...options,
			...(chartContainerRef?.current?.offsetWidth && {
					xAxis: {
						...options.xAxis,
						axisLabel: {
							...options.xAxis.axisLabel,
						}
					}
				})
		};

  return (
    <Box sx={{ borderRadius: '5px', backgroundColor: '#003771', ...sx }} p={2}>
		  <Box sx={{ backgroundColor: '#003771' }} {...containerProps}>
			 <Stack
				  direction="row"
				  justifyContent="space-between"
				  alignItems="center"
				  sx={{ marginBottom: isHappySignal ? 4 : 2 }}
			  >
				  <Typography variant="widgetTitle">
					  {title}
				  </Typography>

				 {/* Top 10 / Bottom 10 Toggle */}
				  {setShowBottom10 && (
					  <Stack direction="row" alignItems="center" spacing={1}>
						  <Typography variant="bodyText" fontWeight={!showBottom10 ? 500 : 400}>
							  Top 10
						  </Typography>
						  <Switch
							  color={showBottom10 ? "info" : "warning"}
							  onChange={(e) => setShowBottom10(e.target.checked)}
							  checked={showBottom10}
							  size="small"
						  />
						  <Typography variant="bodyText" fontWeight={showBottom10 ? 500 : 400}>
							  Bottom 10
						  </Typography>
					  </Stack>
				  )}
			  </Stack>
			<Box sx={{ width: "100%", height: isCost ? 319 : 350 }} key={windowSize} ref={chartContainerRef}>
			{!isLoading ? (			
			  <ReactECharts option={adjustedOption} style={{ width: "100%", height: "100%" }} />
			) : (
			  <LoaderComponent pt={20}/>
			)}
			</Box>
		  </Box>
		</Box>
  );
};

export default CustomBarChartWidget;
